/*
 * BNO reviews carousel — ONE card visible, header arrows.
 *
 * A simplified fork of the home-showcase carousel (assets/bno-home-showcase.js).
 * That one pages a fixed count with a dots pill; this one shows exactly one review
 * at a time and is paged by prev/next arrow BUTTONS that live in the section header
 * (top-right). The arrows LOOP: next on the last card wraps to the first, prev on
 * the first wraps to the last — so they are never disabled (the arrow row is only
 * hidden entirely when there is a single card).
 *
 *   - One card per view: each card is 100% of the content column, scroll-snap start.
 *   - Arrows (data-dir="prev" / "next") go to index ±1, wrapping around the ends.
 *   - Left/Right arrow keys page while the carousel has focus.
 *   - Native horizontal scroll (touch/trackpad) + mouse drag-to-scroll.
 *   - The active index re-syncs from manual swipes/drags (nearest card).
 *   - prefers-reduced-motion: paging jumps instantly.
 */
class BnoReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.bno-reviews__track');
    this.cards = Array.from(this.querySelectorAll('.bno-reviews__card'));
    this.prevBtn = this.querySelector('.bno-reviews__arrow[data-dir="prev"]');
    this.nextBtn = this.querySelector('.bno-reviews__arrow[data-dir="next"]');

    if (!this.track || this.cards.length === 0) return;

    this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.index = 0;
    /** @type {number | null} */
    this.scrollRaf = null;

    this.onScroll = this.onScroll.bind(this);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.onKeydown = this.onKeydown.bind(this);
    this.addEventListener('keydown', this.onKeydown);

    this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
    this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));

    this.#setupDrag();

    // With a single card the arrows have nothing to do — hide the row.
    if (this.cards.length <= 1) {
      const arrows = this.querySelector('.bno-reviews__arrows');
      if (arrows) arrows.hidden = true;
    }

    this.syncArrows();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.removeEventListener('keydown', this.onKeydown);
    if (this.scrollRaf) cancelAnimationFrame(this.scrollRaf);
  }

  /** Scroll so card `i` rests at the track's left edge. `i` wraps around the ends
   * (prev on the first card -> last, next on the last -> first), so the arrows loop.
   * Drag/scroll sync sets `this.index` directly and never routes through here. */
  goTo(i) {
    const n = this.cards.length;
    i = ((i % n) + n) % n;
    this.index = i;
    const card = this.cards[i];
    if (card) {
      const left =
        this.track.scrollLeft +
        (card.getBoundingClientRect().left - this.track.getBoundingClientRect().left);
      this.track.scrollTo({ left: Math.max(0, left), behavior: this.reduce.matches ? 'auto' : 'smooth' });
    }
    this.syncArrows();
  }

  onScroll() {
    if (this.scrollRaf) return;
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = null;
      this.updateIndexFromScroll();
    });
  }

  /** Keep the active index in sync with manual swipes (nearest card to the left edge). */
  updateIndexFromScroll() {
    const target = this.track.getBoundingClientRect().left;
    let best = 0;
    let bestDist = Infinity;
    this.cards.forEach((card, i) => {
      const d = Math.abs(card.getBoundingClientRect().left - target);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    });
    this.index = best;
    this.syncArrows();
  }

  /** Arrows loop, so they are never disabled here — the single-card case hides the
   * whole arrow row in connectedCallback instead. Kept as a hook for future state. */
  syncArrows() {}

  /** @param {KeyboardEvent} event */
  onKeydown(event) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.index - 1);
    }
  }

  /** Mouse drag-to-scroll. Touch keeps native scrolling. */
  #setupDrag() {
    const track = this.track;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let captured = false;
    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      down = true;
      moved = false;
      captured = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) > 4) {
        moved = true;
        track.style.scrollSnapType = 'none';
        try {
          track.setPointerCapture(pointerId);
          captured = true;
        } catch (_) {
          /* ignore */
        }
      }
      if (moved) track.scrollLeft = startScroll - dx;
    });

    const end = () => {
      if (!down) return;
      down = false;
      if (moved) {
        track.style.scrollSnapType = '';
        if (captured) {
          try {
            track.releasePointerCapture(pointerId);
          } catch (_) {
            /* ignore */
          }
        }
        requestAnimationFrame(() => this.updateIndexFromScroll());
      }
    };

    track.addEventListener('pointerup', end);
    track.addEventListener('pointercancel', end);
    track.addEventListener(
      'click',
      (e) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
    track.addEventListener('dragstart', (e) => e.preventDefault());
  }
}

if (!customElements.get('bno-reviews-carousel')) {
  customElements.define('bno-reviews-carousel', BnoReviewsCarousel);
}
